import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link as ReactRouterLink } from 'react-router-dom';
import PptxGenJS from 'pptxgenjs';
import { gql, NetworkStatus } from '@apollo/client';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import { useAnimatedValue } from '../../Hooks/useAnimatedValue';
import { FETCH_EVENT, EVENT_SCORE_PROGRESS_STATS_SUMMARY, EXPORT_SCORE_SUMMARY } from '../../GraphQL/Queries';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import { Provider } from '../../Chakra';

const VALIDATE_SCORES_QUERY = gql`
  query validateScores($eventId: Int!) {
    fetchEventDetail(id: $eventId) {
      id
      title
      fetchEventCompetitions {
        id
        title
        rounds {
          id
          name
          warnings
        }
      }
    }
  }
`;
const VALIDATE_OVERALL_SCORES_QUERY = gql`
  query validateScores($eventId: Int!) {
    fetchEventDetail(id: $eventId) {
      id
      scorekeepingWarnings
    }
  }
`;

const addMasterSlide = (presentation, templateTitle, textPlaceholders) => {
  const placeholders = textPlaceholders.map((textTitle, idx) => ({
    placeholder: {
      options: {
        name: textTitle,
        type: 'title',
        x: 0.6,
        y: 1.5 + (idx * 1.5),
        w: 12,
        h: 5.25,
      },
      text: `placeholder ${textTitle}`,
    },
  }));

  presentation.defineSlideMaster({
    title: templateTitle,
    background: { color: 'FFFFFF' },
    objects: placeholders,
  });
};

const addWinnerSlide = (presentation, winner, masterName, includeRegion) => {
  if (!winner) {
    return;
  }

  const { judgableName, schoolName, regionName } = winner;
  const placeSlide = presentation.addSlide(masterName);
  placeSlide.addText(judgableName, { placeholder: 'student_name' });
  placeSlide.addText(schoolName, { placeholder: 'school_name' });
  if (includeRegion) {
    placeSlide.addText(regionName, { placeholder: 'region_name' });
  }
};

const buildPowerPointTemplate = (event, content) => {
  const showRegionInfo = event.qualifiedAttendingPreviousEvent;

  const presentation = new PptxGenJS();

  const { competitionWinners, overallWinners } = content;

  addMasterSlide(presentation, 'Name of Event', ['event_title']);
  addMasterSlide(presentation, 'Category', ['category_name']);
  addMasterSlide(presentation, 'Competition', ['competition_name']);
  addMasterSlide(presentation, 'First Place', ['student_name', 'school_name', 'region_name']);
  addMasterSlide(presentation, 'Second Place', ['student_name', 'school_name', 'region_name']);
  addMasterSlide(presentation, 'Third Place', ['student_name', 'school_name', 'region_name']);
  addMasterSlide(presentation, 'First Place School', ['school_name', 'region_name']);
  addMasterSlide(presentation, 'Second Place School', ['school_name', 'region_name']);
  addMasterSlide(presentation, 'Third Place School', ['school_name', 'region_name']);
  addMasterSlide(presentation, 'First Place Region', ['region_name']);
  addMasterSlide(presentation, 'Second Place Region', ['region_name']);
  addMasterSlide(presentation, 'Third Place Region', ['region_name']);

  const titleSlide = presentation.addSlide('Name of Event');
  titleSlide.addText(event.title, { placeholder: 'event_title' });

  for (let i = 0; i < competitionWinners.length; i += 1) {
    const { categoryName, competitions } = competitionWinners[i];
    const categorySlide = presentation.addSlide('Category');
    categorySlide.addText(categoryName, { placeholder: 'category_name' });

    for (let j = 0; j < competitions.length; j += 1) {
      const { competitionName, winners } = competitions[j];
      const competitionSlide = presentation.addSlide('Competition');
      competitionSlide.addText(competitionName, { placeholder: 'competition_name' });

      addWinnerSlide(presentation, winners[2], 'Third Place', showRegionInfo);
      addWinnerSlide(presentation, winners[1], 'Second Place', showRegionInfo);
      addWinnerSlide(presentation, winners[0], 'First Place', showRegionInfo);
    }
  }

  const { topStudents, topSchools, topRegions } = overallWinners;

  const competitionSlide = presentation.addSlide('Competition');
  competitionSlide.addText('Top Overall Student', { placeholder: 'competition_name' });
  addWinnerSlide(presentation, topStudents[2], 'Third Place', showRegionInfo);
  addWinnerSlide(presentation, topStudents[1], 'Second Place', showRegionInfo);
  addWinnerSlide(presentation, topStudents[0], 'First Place', showRegionInfo);

  const schoolSlide = presentation.addSlide('Competition');
  schoolSlide.addText('Top Overall School', { placeholder: 'competition_name' });
  addWinnerSlide(presentation, topSchools[2], 'Third Place', showRegionInfo);
  addWinnerSlide(presentation, topSchools[1], 'Second Place', showRegionInfo);
  addWinnerSlide(presentation, topSchools[0], 'First Place', showRegionInfo);

  if (showRegionInfo) {
    const regionSlide = presentation.addSlide('Competition');
    regionSlide.addText('Top Overall Region', { placeholder: 'competition_name' });
    addWinnerSlide(presentation, topRegions[2], 'Third Place', showRegionInfo);
    addWinnerSlide(presentation, topRegions[1], 'Second Place', showRegionInfo);
    addWinnerSlide(presentation, topRegions[0], 'First Place', showRegionInfo);
  }

  presentation.writeFile(`${event.title}_Awards.pptx`);
};

const Warnings = ({ warnings, overallWarnings, eventId }) => {
  if (warnings.length === 0 && overallWarnings.length === 0) {
    return (
      <Alert status="success">
        <AlertIcon />
        <AlertTitle>No warnings found in score validation.</AlertTitle>
      </Alert>
    );
  }
  return (
    <Box backgroundColor="orange.50" marginBlockEnd={4}>
      <Alert status="warning" marginBlockEnd={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Warnings</AlertTitle>
          <AlertDescription>
            We’ve identified discrepancies within one or more competitions.
          </AlertDescription>
        </Box>
      </Alert>
      {overallWarnings.map((warning, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={idx} marginBlockEnd={2} paddingInlineStart={4} borderLeft="4px solid" borderColor="orange.300">
          <Box as="p" marginInlineStart={4}>
            <Text>{warning}</Text>
          </Box>
        </Box>
      ))}
      {warnings.map((c) => (
        <VStack key={c.competition.id} padding={4} spacing={2} align="stretch">
          <Link as={ReactRouterLink} to={`/events/${eventId}/scoring/${c.competition.id}`} color="blue.500">
            <HStack alignItems="center">
              <Text fontSize="md" fontWeight="semibold">
                {c.competition.name}
              </Text>
              <Icon size="md" as={FaChevronRight} />
            </HStack>
          </Link>
          {c.rounds.map((r) => (
            <Box key={r.id} marginBlockEnd={2} paddingInlineStart={4} borderLeft="4px solid" borderColor="orange.300">
              <Text fontSize="md" fontWeight="medium" marginBlockEnd={1}>{r.name}</Text>
              <Box as="ul" marginInlineStart={4}>
                {r.warnings.map((w) => <li key={w}><Text>{w}</Text></li>)}
              </Box>
            </Box>
          ))}
        </VStack>
      ))}
    </Box>
  );
};
Warnings.propTypes = {
  overallWarnings: PropTypes.arrayOf(PropTypes.string).isRequired,
  warnings: PropTypes.arrayOf(PropTypes.shape({
    competition: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    rounds: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      warnings: PropTypes.arrayOf(PropTypes.string),
    })),
  })).isRequired,
  eventId: PropTypes.string.isRequired,
};

const ScoreProgressBar = ({ progress }) => {
  const progressAmount = useAnimatedValue(0, progress);

  return (
    <Progress colorScheme="orange" value={progressAmount} />
  );
};
ScoreProgressBar.propTypes = {
  progress: PropTypes.number.isRequired,
};

const ScoreProgress = () => {
  const { id } = useParams();
  const [includeOverallWarnings, setIncludeOverallWarnings] = useState(false);
  const { data: eventDetail } = useQuery(
    FETCH_EVENT,
    {
      variables: { id: parseInt(id, 10) },
    },
  );
  const event = eventDetail?.fetchEventDetail ?? {};

  const { data: summary, loading: summaryLoading } = useQuery(
    EVENT_SCORE_PROGRESS_STATS_SUMMARY,
    {
      fetchPolicy: 'network-only',
      variables: { eventId: parseInt(id, 10) },
    },
  );
  const scoreProgressSummary = summary?.scoredCompetitions ?? {};

  const formatRef = useRef('text');
  const format = formatRef.current;
  const [exportScore, { loading: exportScoresLoading }] = useLazyQuery(EXPORT_SCORE_SUMMARY, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      if (response.exportScore.format_type === 'text') {
        const extension = format === 'csv' ? 'csv' : 'txt';
        const file = new Blob(response.exportScore.content, { type: 'text/plain' });
        const element = document.createElement('a');
        element.href = URL.createObjectURL(file);
        element.download = `${event.title}_score.${extension}`;
        document.body.appendChild(element); // Required for this to work in FireFox
        element.click();
      } else if (response.exportScore.format_type === 'power_point_template') {
        buildPowerPointTemplate(event, response.exportScore.content);
      } else {
        const presentation = new PptxGenJS();
        const { content } = response.exportScore;
        content.forEach((lines) => {
          const slide = presentation.addSlide();
          const textboxOpts = {
            x: 1, y: 1, color: '363636', fill: 'ffffff', align: 'left',
          };
          const textboxText = lines.toString();
          slide.addText(textboxText, textboxOpts);
        });
        presentation.writeFile(`${event.title}_Score.pptx`);
      }
    },
  });

  const validationTime = useRef(new Date());
  const [validateCompetitionScores, {
    called: validateCompetitionScoresCalled,
    data: validateCompetitionScoresData,
    loading: validateCompetitionScoresLoading,
    networkStatus,
  }] = useLazyQuery(VALIDATE_SCORES_QUERY, {
    fetchPolicy: 'network-only',
    variables: { eventId: parseInt(id, 10) },
    onCompleted: (response) => {
      validationTime.current = new Date();
    },
    notifyOnNetworkStatusChange: true,
  });
  const [validateOverallScores, {
    data: validateOverallData,
    loading: validateOverallLoading,
    networkStatus: overallNetworkStatus,
  }] = useLazyQuery(VALIDATE_OVERALL_SCORES_QUERY, {
    fetchPolicy: 'network-only',
    variables: { eventId: parseInt(id, 10) },
    onCompleted: () => {
      validationTime.current = new Date();
    },
    notifyOnNetworkStatusChange: true,
  });
  const validateScoresLoading = (
    validateCompetitionScoresLoading || validateOverallLoading
  );
  const validateScoresRefetching = (
    networkStatus === NetworkStatus.refetch
    || overallNetworkStatus === NetworkStatus.refetch
  );

  const overallWarnings = validateOverallData
    ? validateOverallData.fetchEventDetail?.scorekeepingWarnings
    : [];

  const competitionWarnings = validateCompetitionScoresData
    ? validateCompetitionScoresData.fetchEventDetail?.fetchEventCompetitions
      .filter((c) => c.rounds.some((r) => r.warnings && r.warnings.length > 0))
      .map((c) => ({
        competition: { name: c.title, id: c.id },
        rounds: c.rounds.filter((r) => r.warnings && r.warnings.length > 0)
          .map((r) => ({ id: r.id, name: r.name, warnings: r.warnings })),
      }))
    : [];

  const refetchWarnings = () => {
    validateCompetitionScores();
    if (includeOverallWarnings) {
      validateOverallScores();
    }
  };

  useHeaderComponentsMutation({
    title: 'SCORING PROGRESS',
    backLink: `/event-manage/${id}`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  const exportScoreHandler = (fmt) => {
    formatRef.current = fmt;
    exportScore({ variables: { eventId: parseInt(id, 10), format: fmt } });
  };

  if (summaryLoading) {
    return null;
  }

  const completed = scoreProgressSummary.completed_total_competitions;
  const total = scoreProgressSummary.total_competitions;
  const progressAmount = 100 * (completed / total);

  return (
    <VStack align="stretch" spacing={6} minHeight="70vh">
      <Heading marginBlockStart={4}> Competitions Scored </Heading>
      <Box>
        <ScoreProgressBar progress={progressAmount} />
        <HStack justifyContent="space-between">
          <Link as={ReactRouterLink} to={`/events/${id}/complete-competition`} color="blue.500">
            {scoreProgressSummary.completed_total_competitions}
            {' '}
            Completed
          </Link>
          <Link as={ReactRouterLink} to={`/events/${id}/incomplete-competition`} color="blue.500">
            {scoreProgressSummary.inprogress_total_competitions}
            {' '}
            Incompleted
          </Link>
        </HStack>
      </Box>
      <VStack align="stretch" height="100%" spacing={1}>
        <Flex gap={4} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }}>
          <Text color="gray.500">
            {validateCompetitionScoresCalled && (
              (validateScoresLoading || validateScoresRefetching)
                ? 'Validating scores...'
                : `Last validated at: ${validationTime.current.toLocaleString()}`
            )}
          </Text>
          <Spacer />
          <Button
            variant="outline"
            colorScheme="orange"
            onClick={() => refetchWarnings()}
            isLoading={validateScoresLoading || validateScoresRefetching}
            isDisabled={exportScoresLoading || validateScoresLoading || validateScoresRefetching}
          >
            {validateCompetitionScoresData ? 'Revalidate Scores' : 'Validate Scores'}
          </Button>
          <Menu>
            <MenuButton
              as={Button}
              colorScheme="orange"
              isLoading={exportScoresLoading}
              isDisabled={
                exportScoresLoading
                || validateScoresLoading
                || validateScoresRefetching
                || !validateCompetitionScoresCalled
              }
              rightIcon={<FaChevronDown />}
            >
              Export as...
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => exportScoreHandler('text')}>Text</MenuItem>
              <MenuItem onClick={() => exportScoreHandler('power_point_template')}>Power Point Template</MenuItem>
              <MenuItem onClick={() => exportScoreHandler('script')}>Script</MenuItem>
              <MenuItem onClick={() => exportScoreHandler('debug')}>Debug</MenuItem>
              <MenuItem onClick={() => exportScoreHandler('csv')}>CSV</MenuItem>
              <MenuItem onClick={() => exportScoreHandler('anonymized')}>Anonymized</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        <HStack width="100%" justifyContent="flex-end">
          <Text>Include Warnings for Top Overall Students</Text>
          <Checkbox
            isChecked={includeOverallWarnings}
            onChange={(e) => setIncludeOverallWarnings(e.target.checked)}
            isDisabled={validateScoresLoading || validateScoresRefetching}
          />
        </HStack>
      </VStack>
      {(!validateScoresLoading) && validateCompetitionScoresCalled && (
        <Warnings
          warnings={competitionWarnings}
          overallWarnings={overallWarnings}
          eventId={id}
        />
      )}
    </VStack>
  );
};

export default function ScoreProgressPage() {
  return (
    <Provider>
      <ScoreProgress />
    </Provider>
  );
}
